import React from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  ArrowForward,
  ContentCopy as ContentCopyIcon,
  Publish as PublishIcon,
  Check as CheckIcon,
  AnalyticsOutlined as Module1Icon,
  CodeOutlined as Module2Icon,
  CategoryOutlined as Module3Icon,
  SettingsOutlined as Module4Icon,
} from '@mui/icons-material';

interface ModuleActionCardProps {
  title?: string;
  isExporting?: boolean;
  exportSuccess?: boolean;
  onExport?: () => void;
  onViewExportResults?: () => void;
  onGoToNextModule?: () => void;
  exportButtonText?: string;
  viewResultsButtonText?: string;
  nextModuleButtonText?: string;
  nextModuleNumber?: number;
  viewResultsButtonIcon?: React.ReactNode;
  viewResultsButtonDisabled?: boolean;
  viewResultsButtonColor?: "inherit" | "primary" | "secondary" | "success" | "error" | "info" | "warning";
  icon?: React.ReactNode;
  disabled?: boolean; // New prop to disable all buttons
}

const ModuleActionCard: React.FC<ModuleActionCardProps> = ({
  title,
  isExporting = false,
  exportSuccess = false,
  onExport,
  onViewExportResults,
  onGoToNextModule,
  exportButtonText = 'Export',
  viewResultsButtonText = 'View Export Results',
  nextModuleButtonText = 'Go to Next Module',
  nextModuleNumber,
  viewResultsButtonIcon,
  viewResultsButtonDisabled = false,
  viewResultsButtonColor = "primary",
  icon,
  disabled = false, // Default to enabled
}) => {
  // Determine the module text to display
  const moduleText = nextModuleNumber ? `Continue to Module ${nextModuleNumber}` : title || 'Continue to Next Module';

  // Determine the appropriate icon based on the next module number
  const getModuleIcon = () => {
    if (icon) return icon;
    
    switch (nextModuleNumber) {
      case 1: return <Module1Icon />;
      case 2: return <Module2Icon />;
      case 3: return <Module3Icon />;
      case 4: return <Module4Icon />;
      default: return <AssignmentIcon />;
    }
  };

  const moduleIcon = getModuleIcon();

  return (
    <Card 
      sx={{ 
        mt: 4,
        position: 'relative',
        overflow: 'visible',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: exportSuccess 
            ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
            : 'linear-gradient(90deg, #332E5C 0%, #00CED1 100%)',
          borderRadius: '20px 20px 0 0',
        }
      }}
    >
      <CardContent sx={{ pt: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          minHeight: '64px'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 3, 
              bgcolor: exportSuccess ? 'rgba(16, 185, 129, 0.12)' : 'rgba(51, 46, 92, 0.1)',
              mr: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${exportSuccess ? 'rgba(16, 185, 129, 0.2)' : 'rgba(51, 46, 92, 0.15)'}`
            }}>
              {React.cloneElement(moduleIcon as React.ReactElement, { 
                sx: { 
                  color: exportSuccess ? '#10b981' : '#332E5C',
                  fontSize: '1.5rem'
                } 
              })}
            </Box>
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  color: '#1f2937',
                  mb: 0.5
                }}
              >
                {moduleText}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#6b7280',
                  fontSize: '0.875rem'
                }}
              >
                {exportSuccess ? 'Ready to proceed to next step' : 'Complete this module to continue'}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            {onExport && (
              <Button
                variant="contained"
                onClick={onExport}
                disabled={disabled || isExporting || exportSuccess}
                startIcon={isExporting ? <CircularProgress size={18} color="inherit" /> : <PublishIcon />}
                sx={{
                  minWidth: '140px',
                  height: '44px',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  background: exportSuccess 
                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                    : 'linear-gradient(135deg, #332E5C 0%, #221B3F 100%)',
                  '&:hover': {
                    background: exportSuccess 
                      ? 'linear-gradient(135deg, #059669 0%, #047857 100%)' 
                      : 'linear-gradient(135deg, #4A4570 0%, #332E5C 100%)',
                    boxShadow: exportSuccess
                      ? '0 8px 15px -3px rgba(16, 185, 129, 0.3), 0 4px 6px -2px rgba(16, 185, 129, 0.2)'
                      : '0 8px 15px -3px rgba(51, 46, 92, 0.3), 0 4px 6px -2px rgba(51, 46, 92, 0.2)'
                  },
                  '&:disabled': {
                    background: '#e5e7eb',
                    color: '#9ca3af'
                  }
                }}
              >
                {isExporting ? 'Processing...' : exportSuccess ? 'Completed' : exportButtonText}
              </Button>
            )}

            {exportSuccess && onViewExportResults && (
              <Button
                variant="outlined"
                color={viewResultsButtonColor}
                onClick={onViewExportResults}
                disabled={disabled || viewResultsButtonDisabled}
                startIcon={viewResultsButtonIcon || <ContentCopyIcon />}
                sx={{
                  minWidth: '140px',
                  height: '44px',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  borderWidth: '1.5px',
                  '&:hover': {
                    borderWidth: '1.5px'
                  }
                }}
              >
                {viewResultsButtonText}
              </Button>
            )}

            {exportSuccess && onGoToNextModule && (
              <Button
                variant="contained"
                onClick={onGoToNextModule}
                disabled={disabled}
                startIcon={<ArrowForward />}
                sx={{
                  minWidth: '160px',
                  height: '44px',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  background: 'linear-gradient(135deg, #00CED1 0%, #009092 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #33D6D9 0%, #00CED1 100%)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 8px 15px -3px rgba(0, 206, 209, 0.4), 0 4px 6px -2px rgba(0, 206, 209, 0.3)'
                  }
                }}
              >
                {nextModuleButtonText}
              </Button>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ModuleActionCard;
